/**
 * Build Your Box — mix flavors, quantity packs, subscribe vs one-time.
 */
(function () {
  function formatMoney(cents, moneyFormat) {
    if (cents == null || cents === '') return '';
    if (typeof cents === 'string' && cents.trim().startsWith('$')) return cents.trim();
    const value = Number(cents);
    if (Number.isNaN(value)) return String(cents);

    const format = moneyFormat || window.theme?.moneyFormat || window.shopMoneyFormat || '${{amount}}';
    const amount = (Math.round(value) / 100).toFixed(2);
    const amountNoDecimals = String(Math.round(value / 100));
    const amountComma = amount.replace('.', ',');

    if (format && format.includes('{{')) {
      return format
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimals)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountComma)
        .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, amount.replace('.', "'"))
        .replace(/\{\{\s*amount\s*\}\}/g, amount);
    }

    try {
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(value, format);
      }
    } catch (e) {
      /* fall through */
    }
    return `$${amount}`;
  }

  function parseJarCount(value) {
    const match = String(value || '').match(/(\d+)/);
    return match ? Number(match[1]) : 0;
  }

  class BuildYourBox extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      try {
        this.config = JSON.parse(this.querySelector('[data-byb-config]')?.textContent || '{}');
      } catch (e) {
        this.config = {};
      }

      this.quantities = {};
      (this.config.flavors || []).forEach((flavor) => {
        this.quantities[String(flavor.id)] = 0;
      });

      this.packSize = Number(this.config.defaultPack) || 4;
      this.purchaseMode = 'subscribe';
      this.sellingPlanId = '';

      this.cacheDom();
      this.initPackFromDom();
      this.bindEvents();
      this.hideInjectedSubscriptionWidgets();
      this.neutralizeAppstleHijack();
      this.observeSeal();
      this.syncPlanOptions();
      this.render();
    }

    disconnectedCallback() {
      this._sealObserver?.disconnect();
      clearTimeout(this._slotTooltipTimer);
    }

    cacheDom() {
      this.progressCount = this.querySelector('[data-byb-progress-count]');
      this.progressHint = this.querySelector('[data-byb-progress-hint]');
      this.progressFill = this.querySelector('[data-byb-progress-fill]');
      this.slotsEl = this.querySelector('[data-byb-slots]');
      this.checklistEls = this.querySelectorAll('[data-byb-checklist]');
      this.totalPriceEl = this.querySelector('[data-byb-total-price]');
      this.totalCompareEl = this.querySelector('[data-byb-total-compare]');
      this.totalSaveEl = this.querySelector('[data-byb-total-save]');
      this.subscribePercentEls = this.querySelectorAll('[data-byb-subscribe-percent]');
      this.atcBtn = this.querySelector('[data-byb-atc]');
      this.atcLabelEl = this.atcBtn?.querySelector('[data-byb-atc-label]');
      this.atcLabel = this.config.atcLabel || this.atcLabelEl?.textContent?.trim() || 'ADD TO CART';
      this.errorEl = this.querySelector('[data-byb-error]');
      this.planSelect = this.querySelector('[data-byb-plan-select]');
      this.planInput = this.querySelector('[data-byb-selling-plan]');
    }

    initPackFromDom() {
      const checked = this.querySelector('[data-byb-pack]:checked');
      if (!checked) return;
      this.packSize = Number(checked.dataset.jars) || parseJarCount(checked.value) || this.packSize;
    }

    bindEvents() {
      this.addEventListener('click', (event) => {
        const qtyBtn = event.target.closest('[data-byb-qty]');
        if (qtyBtn) {
          event.preventDefault();
          const card = qtyBtn.closest('[data-byb-flavor]');
          const flavorId = card?.dataset.bybFlavor;
          const action = qtyBtn.dataset.bybQty;
          if (!flavorId || !action) return;
          this.adjustQty(flavorId, action === 'plus' ? 1 : -1);
          return;
        }

        const emptySlot = event.target.closest('[data-byb-empty-slot]');
        if (emptySlot) {
          event.preventDefault();
          this.showEmptySlotTooltip(emptySlot);
          return;
        }

        const slot = event.target.closest('[data-byb-slot][data-flavor-id]');
        if (slot?.dataset.flavorId) this.adjustQty(slot.dataset.flavorId, -1);
      });

      this.addEventListener('change', (event) => {
        const packInput = event.target.closest('[data-byb-pack]');
        if (packInput) {
          this.packSize = Number(packInput.dataset.jars) || parseJarCount(packInput.value) || this.packSize;
          this.trimOverflow();
          this.syncPlanOptions();
          this.render();
          return;
        }

        const modeInput = event.target.closest('[data-byb-mode]');
        if (modeInput) {
          this.purchaseMode = modeInput.value;
          this.render();
          return;
        }

        if (event.target.matches('[data-byb-plan-select]')) {
          this.sellingPlanId = event.target.value;
          this.render();
        }
      });

      this.atcBtn?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
        this.addToCart();
      });
    }

    hideInjectedSubscriptionWidgets() {
      this.querySelectorAll(
        '.sealsubs-container, .sealsubs-target-element, [data-seal-extension], .appstle_subscription_widget, .appstle_sub_widget, .appstle-subscription-widget, [id^="appstle_subscription"], [class*="appstle_subscription_widget"]'
      ).forEach((el) => {
        el.style.display = 'none';
        el.querySelectorAll('input, select, textarea').forEach((field) => {
          field.disabled = true;
          if (field.getAttribute('name') === 'selling_plan') field.removeAttribute('name');
        });
      });
    }

    neutralizeAppstleHijack() {
      if (document.documentElement.dataset.appstleHijackBound === 'true') return;
      document.documentElement.dataset.appstleHijackBound = 'true';
      const stop = (event) => {
        event.stopImmediatePropagation();
        if (typeof event.preventDefault === 'function') event.preventDefault();
      };
      [
        'AppstleSubscription:SubscriptionWidget:AddToCartIntent',
        'appstle:add-to-cart',
        'appstle:addtocart',
      ].forEach((name) => document.addEventListener(name, stop, true));
    }

    observeSeal() {
      if (this._sealObserver) return;
      this._sealObserver = new MutationObserver(() => this.hideInjectedSubscriptionWidgets());
      this._sealObserver.observe(this, { childList: true, subtree: true });
    }

    money(cents) {
      return formatMoney(cents, this.config?.moneyFormat);
    }

    getSelectedPack() {
      const packs = this.config.packs || [];
      return packs.find((pack) => Number(pack.jars) === Number(this.packSize)) || packs[0] || null;
    }

    getSortedPacks() {
      return (this.config.packs || []).slice().sort((a, b) => Number(a.jars) - Number(b.jars));
    }

    getMaxPackSize() {
      const packs = this.getSortedPacks();
      if (!packs.length) return this.packSize;
      return Number(packs[packs.length - 1].jars) || this.packSize;
    }

    findPackForQuantity(qty) {
      const count = Number(qty) || 0;
      return this.getSortedPacks().find((pack) => Number(pack.jars) >= count) || null;
    }

    selectPackSize(jars) {
      const size = Number(jars);
      if (!size || size === this.packSize) return false;
      this.packSize = size;
      const input = this.querySelector(`[data-byb-pack][data-jars="${size}"]`);
      if (input && !input.checked) input.checked = true;
      this.syncPlanOptions();
      return true;
    }

    getTotalSelected() {
      return Object.values(this.quantities).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
    }

    getFlavorById(id) {
      return (this.config.flavors || []).find((flavor) => String(flavor.id) === String(id));
    }

    getSelectedFlavorEntries() {
      return Object.entries(this.quantities)
        .map(([id, qty]) => ({ flavor: this.getFlavorById(id), qty: Number(qty) || 0 }))
        .filter((entry) => entry.flavor && entry.qty > 0);
    }

    getFlavorPlanPool() {
      const packPlans = this.getPackPlanPool();
      if (packPlans.length) return packPlans;

      const plans = [];
      const seen = new Set();
      (this.config.flavors || []).forEach((flavor) => {
        (flavor.selling_plans || []).forEach((plan) => {
          const id = String(plan?.id || '');
          if (!id || seen.has(id) || this.isSealPlan(plan)) return;
          seen.add(id);
          plans.push(plan);
        });
      });
      return plans.length ? plans : (this.config.flavors || []).flatMap((flavor) => flavor.selling_plans || []);
    }

    planFrequencyKey(name) {
      const text = String(name || '').toLowerCase().replace(/\s+/g, ' ');
      if (/(2|two)\s*weeks?/.test(text)) return '2w';
      if (/(3|three)\s*months?/.test(text)) return '3m';
      if (/(2|two)\s*months?/.test(text)) return '2m';
      if (text.includes('week')) return '1w';
      if (text.includes('month')) return '1m';
      return text;
    }

    isAppstlePlan(plan) {
      return String(plan?.app_id || '').toLowerCase().includes('appstle');
    }

    isSealPlan(plan) {
      return String(plan?.app_id || '').toLowerCase().includes('seal');
    }

    getPackPlanPool() {
      const table = this.config.packPlans || {};
      const exact = table[String(this.packSize)] || [];
      if (exact.length) return exact.filter((plan) => !this.isSealPlan(plan));
      const appstle = Object.values(table)
        .flat()
        .filter((plan) => this.isAppstlePlan(plan));
      return appstle;
    }

    getPlanForFlavor(flavor) {
      const all = flavor?.selling_plans || [];
      const appstle = all.filter((plan) => this.isAppstlePlan(plan) || !this.isSealPlan(plan));
      const pool = appstle.length ? appstle : all;
      const selectedId = String(this.sellingPlanId || '');
      const packPlan = this.getPackPlanPool().find((plan) => String(plan.id) === selectedId);

      if (selectedId) {
        const match = pool.find((plan) => String(plan.id) === selectedId);
        if (match) return match;
      }
      if (packPlan) return packPlan;
      return pool[0] || all[0] || null;
    }

    getFlavorUnitPrice(flavor) {
      return Number(flavor?.price) || 0;
    }

    getFlavorSubscribeUnit(flavor) {
      const unit = this.getFlavorUnitPrice(flavor);
      const plan = this.getPlanForFlavor(flavor);
      if (
        plan &&
        !this.isSealPlan(plan) &&
        unit > 0 &&
        plan.price != null &&
        Number(plan.price) >= 0 &&
        Number(plan.price) < unit
      ) {
        return Number(plan.price);
      }
      const packPlan = this.getPackPlanPool().find((item) => String(item.id) === String(this.sellingPlanId))
        || this.getPackPlanPool()[0];
      const save =
        Number(plan?.save_percent) ||
        Number(packPlan?.save_percent) ||
        this.packSubscribeSavePercent(this.packSize, this.config.fallbackSave);
      if (save > 0 && unit > 0) return Math.round((unit * (100 - save)) / 100);
      return unit;
    }

    savePercent(price, compare) {
      const current = Number(price) || 0;
      const original = Number(compare) || 0;
      if (original <= current || original <= 0) return 0;
      return Math.round(((original - current) * 100) / original);
    }

    packSubscribeSavePercent(jars, fallback) {
      // Always show marketed pack % (15/20/25), not Appstle's live adjustment.
      const qty = Number(jars) || 0;
      if (qty === 4) return 15;
      if (qty === 8) return 20;
      if (qty === 12) return 25;
      return Math.max(0, Number(fallback) || 0);
    }

    getPackSalePrice(jars, unitPrice) {
      const count = Math.max(1, Number(jars) || 1);
      const unit = Number(unitPrice) || 0;
      const table = this.config.packSalePrices || {};
      const listed = Number(table[String(count)]);
      if (listed > 0) {
        const refUnit = Number(this.config.unitPrice) || 0;
        if (
          this.config.scalePackPrices &&
          refUnit > 0 &&
          unit > 0 &&
          unit !== refUnit
        ) {
          return Math.round((listed * unit) / refUnit);
        }
        return listed;
      }
      return unit * count;
    }

    getSubscribePackPrice(packPrice, jars = this.packSize) {
      const count = Math.max(1, Number(jars) || this.packSize || 1);
      const pool = (this.config.packPlans || {})[String(count)] || [];
      const selected =
        pool.find((plan) => String(plan.id) === String(this.sellingPlanId)) || pool[0];
      if (selected && Number(selected.price) > 0) {
        return Number(selected.price);
      }

      const table = this.config.packSubscribePrices || {};
      const listed = Number(table[String(count)]);
      if (listed > 0) return listed;

      const price = Number(packPrice) || 0;
      if (price <= 0) return 0;
      const pct = this.packSubscribeSavePercent(count, this.config.fallbackSave);
      if (pct > 0) return Math.round((price * (100 - pct)) / 100);
      return price;
    }

    getBoxTotals(jars) {
      const count = Math.max(1, Number(jars) || 1);
      const unit = this.getBaseUnitPrice();
      const packMeta = (this.config.packs || []).find((pack) => Number(pack.jars) === count);
      const oneTime = this.getPackSalePrice(count, unit);
      const compareAt = Number(packMeta?.compare_at_price) || 0;
      const original = compareAt > oneTime ? compareAt : oneTime;
      const subscribePrice = this.getSubscribePackPrice(oneTime, count);
      const oneTimeSave = this.savePercent(oneTime, original);
      const subscribeSave = this.savePercent(subscribePrice, original);
      return {
        oneTime,
        compareAt: original,
        subscribePrice,
        oneTimeSave,
        subscribeSave,
      };
    }

    getBaseFlavor() {
      const selected = this.getSelectedFlavorEntries();
      if (selected.length) return selected[0].flavor;
      return (this.config.flavors || [])[0] || null;
    }

    getBaseUnitPrice() {
      const selected = this.getSelectedFlavorEntries();
      if (selected.length) {
        const qty = selected.reduce((sum, entry) => sum + entry.qty, 0);
        const total = selected.reduce((sum, entry) => sum + this.getFlavorUnitPrice(entry.flavor) * entry.qty, 0);
        if (qty > 0) return Math.round(total / qty);
      }
      return this.getFlavorUnitPrice(this.getBaseFlavor());
    }

    adjustQty(flavorId, delta) {
      const id = String(flavorId);
      const current = Number(this.quantities[id]) || 0;
      let next = current + delta;
      if (next < 0) next = 0;

      const projectedTotal = this.getTotalSelected() - current + next;

      if (delta > 0) {
        const maxPack = this.getMaxPackSize();
        if (projectedTotal > maxPack) {
          this.showError(`Your ${maxPack}-jar box is full. Remove a jar to add a different flavor.`);
          return;
        }

        if (projectedTotal > this.packSize) {
          const nextPack = this.findPackForQuantity(projectedTotal);
          if (!nextPack) {
            this.showError(`Your ${this.packSize}-jar box is full. Remove a jar or choose a larger box.`);
            return;
          }
          this.selectPackSize(nextPack.jars);
        }
      }

      this.quantities[id] = next;
      this.clearError();
      this.render();
    }

    trimOverflow() {
      let total = this.getTotalSelected();
      if (total <= this.packSize) return;

      const ids = Object.keys(this.quantities);
      for (let i = ids.length - 1; i >= 0 && total > this.packSize; i -= 1) {
        const id = ids[i];
        const qty = Number(this.quantities[id]) || 0;
        if (qty <= 0) continue;
        const remove = Math.min(qty, total - this.packSize);
        this.quantities[id] = qty - remove;
        total -= remove;
      }
    }

    syncPlanOptions() {
      const plans = this.getFlavorPlanPool();
      if (!this.planSelect) return;

      const previousName = this.planSelect.options[this.planSelect.selectedIndex]?.textContent || '';
      const previous = this.sellingPlanId;
      this.planSelect.innerHTML = '';

      if (!plans.length) {
        const option = document.createElement('option');
        option.value = '';
        option.textContent = this.config.fallbackFrequency || 'Every 30 Days';
        this.planSelect.appendChild(option);
        this.sellingPlanId = '';
        return;
      }

      const seen = new Set();
      plans.forEach((plan) => {
        const id = String(plan.id);
        if (!id || seen.has(id)) return;
        seen.add(id);
        const option = document.createElement('option');
        option.value = id;
        option.textContent = plan.name;
        option.dataset.planSave = String(plan.save_percent || 0);
        this.planSelect.appendChild(option);
      });

      const stillValid = [...seen].includes(String(previous));
      const previousKey = this.planFrequencyKey(previousName);
      const byFreq = stillValid
        ? null
        : [...this.planSelect.options].find((option) => this.planFrequencyKey(option.textContent) === previousKey);
      this.sellingPlanId = stillValid
        ? String(previous)
        : byFreq?.value || this.planSelect.options[0]?.value || '';
      this.planSelect.value = this.sellingPlanId;
    }

    getPackCardPricing(jars) {
      const totals = this.getBoxTotals(jars);
      if (this.purchaseMode === 'subscribe') {
        return {
          oneTime: totals.oneTime,
          subscribe: totals.subscribePrice,
          current: totals.subscribePrice,
          compare: totals.compareAt > totals.subscribePrice ? totals.compareAt : 0,
          savePercent:
            totals.compareAt > totals.subscribePrice
              ? this.packSubscribeSavePercent(jars, totals.subscribeSave)
              : totals.subscribeSave,
        };
      }

      return {
        oneTime: totals.oneTime,
        subscribe: totals.subscribePrice,
        current: totals.oneTime,
        compare: totals.compareAt > totals.oneTime ? totals.compareAt : 0,
        savePercent: totals.oneTimeSave,
      };
    }

    getPricing() {
      const totals = this.getBoxTotals(this.packSize);
      const current = this.purchaseMode === 'subscribe' ? totals.subscribePrice : totals.oneTime;
      const compare =
        this.purchaseMode === 'subscribe'
          ? totals.compareAt > totals.subscribePrice
            ? totals.compareAt
            : 0
          : totals.compareAt > totals.oneTime
            ? totals.compareAt
            : 0;
      const savePercent =
        this.purchaseMode === 'subscribe'
          ? this.packSubscribeSavePercent(this.packSize, totals.subscribeSave)
          : totals.oneTimeSave;

      return {
        oneTime: totals.oneTime,
        subscribe: totals.subscribePrice,
        current,
        compare,
        savings: compare > current ? compare - current : 0,
        savePercent,
      };
    }

    getSelectedSubscribeDisplayPercent() {
      return this.packSubscribeSavePercent(this.packSize, this.config.fallbackSave);
    }

    buildSlotList() {
      const slots = [];
      Object.entries(this.quantities).forEach(([id, qty]) => {
        const flavor = this.getFlavorById(id);
        if (!flavor || qty <= 0) return;
        for (let i = 0; i < qty; i += 1) {
          slots.push({
            flavorId: id,
            title: flavor.title,
            image: flavor.image,
          });
        }
      });
      while (slots.length < this.packSize) slots.push(null);
      return slots.slice(0, this.packSize);
    }

    renderCards() {
      const total = this.getTotalSelected();
      this.querySelectorAll('[data-byb-flavor]').forEach((card) => {
        const id = card.dataset.bybFlavor;
        const qty = Number(this.quantities[id]) || 0;
        card.classList.toggle('is-selected', qty > 0);

        const valueEl = card.querySelector('[data-byb-qty-value]');
        if (valueEl) valueEl.textContent = String(qty);

        const minus = card.querySelector('[data-byb-qty="minus"]');
        const plus = card.querySelector('[data-byb-qty="plus"]');
        if (minus) minus.disabled = qty <= 0;
        if (plus) plus.disabled = total >= this.getMaxPackSize();
      });
    }

    hideEmptySlotTooltips() {
      clearTimeout(this._slotTooltipTimer);
      this.querySelectorAll('.byb-slot.is-tooltip-visible').forEach((el) => {
        el.classList.remove('is-tooltip-visible');
      });
    }

    showEmptySlotTooltip(slot) {
      this.hideEmptySlotTooltips();
      slot.classList.add('is-tooltip-visible');
      this._slotTooltipTimer = setTimeout(() => this.hideEmptySlotTooltips(), 2800);
    }

    renderSlots() {
      if (!this.slotsEl) return;
      this.hideEmptySlotTooltips();
      const slots = this.buildSlotList();
      this.slotsEl.innerHTML = slots
        .map((slot, index) => {
          if (!slot) {
            return `<button type="button" class="byb-slot byb-slot--empty" data-byb-slot data-byb-empty-slot aria-label="Select your flavor for slot ${index + 1}">
               <svg class="byb-slot__plus" width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <rect x="0.355002" y="0.355002" width="29.1066" height="29.1066" fill="#E8186A"/>
                  <rect x="0.355002" y="0.355002" width="29.1066" height="29.1066" stroke="#E8186A" stroke-width="0.709917"/>
                  <path d="M14.9116 9.8457C15.0783 9.84578 15.2164 9.9034 15.3276 10.0176C15.439 10.1319 15.4946 10.2716 15.4946 10.4375V14.3213H19.3794C19.5037 14.3214 19.6133 14.3531 19.7085 14.417L19.7983 14.4912C19.9127 14.6045 19.9702 14.7441 19.9702 14.9111C19.9702 15.078 19.9126 15.2169 19.7983 15.3281C19.6842 15.4392 19.545 15.495 19.3794 15.4951H15.4946V19.3789C15.4946 19.5451 15.4381 19.6842 15.3247 19.7969C15.2114 19.9095 15.0718 19.9658 14.9048 19.9658C14.7379 19.9658 14.599 19.9095 14.4878 19.7969C14.3766 19.6842 14.3208 19.5451 14.3208 19.3789V15.4951H10.437C10.2709 15.4951 10.1317 15.4385 10.019 15.3252C9.90631 15.2119 9.8501 15.0714 9.8501 14.9043C9.8502 14.7376 9.90667 14.5994 10.019 14.4883C10.1317 14.377 10.2708 14.3213 10.437 14.3213H14.3208V10.4375C14.3208 10.2717 14.3782 10.1319 14.4917 10.0176C14.605 9.90327 14.7446 9.8457 14.9116 9.8457Z" fill="white" stroke="white" stroke-width="0.0354959"/>
                  </svg>
               <span class="byb-slot__tooltip" role="tooltip">Select your flavor</span>
            </button>`;
          }
          const img = slot.image
            ? `<img src="${slot.image}" alt="${this.escapeAttr(slot.title)}" loading="lazy" width="120" height="120">`
            : '';
          return `<button type="button" class="byb-slot is-filled" data-byb-slot data-flavor-id="${this.escapeAttr(
            slot.flavorId
          )}" aria-label="Remove ${this.escapeAttr(slot.title)}">
            <span class="byb-slot__media">${img}</span>
            <span class="byb-slot__remove" aria-hidden="true">
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.2 1.2l5.6 5.6M6.8 1.2L1.2 6.8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
              </svg>
            </span>
          </button>`;
        })
        .join('');
    }

    renderProgress() {
      const total = this.getTotalSelected();
      const remaining = Math.max(this.packSize - total, 0);
      const percent = this.packSize > 0 ? Math.min((total / this.packSize) * 100, 100) : 0;

      if (this.progressCount) {
        this.progressCount.innerHTML = `Jars selected: <span>${total} / ${this.packSize}</span>`;
      }
      if (this.progressHint) {
        this.progressHint.textContent = remaining === 0 ? 'Box complete' : `Select ${remaining} more`;
      }
      if (this.progressFill) this.progressFill.style.width = `${percent}%`;
    }

    renderChecklist() {
      const flavorsDone = this.getTotalSelected() === this.packSize;
      const purchaseDone = Boolean(this.purchaseMode);

      this.checklistEls.forEach((list) => {
        list.querySelectorAll('[data-check]').forEach((item) => {
          const key = item.dataset.check;
          const done = key === 'pack' || (key === 'flavors' && flavorsDone) || (key === 'purchase' && purchaseDone);
          item.classList.toggle('is-done', done);

          const label = item.querySelector('[data-check-label]');
          if (label) {
            if (key === 'pack') label.textContent = `${this.packSize}-pack selected`;
            if (key === 'flavors') label.textContent = done ? 'Flavors complete' : 'Flavors not complete';
            if (key === 'purchase') label.textContent = done ? 'Purchase type set' : 'Purchase type not set';
          }

          const icon = item.querySelector('[data-check-icon]');
          if (icon) {
            icon.innerHTML = done
              ? `<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.5 8.2L6.4 11.1L12.5 4.9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`
              : `<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;
          }
        });
      });
    }

    renderPackPrices() {
      this.querySelectorAll('[data-byb-pack]').forEach((input) => {
        const jars = Number(input.dataset.jars) || 0;
        if (jars <= 0) return;
        const card = input.closest('.byb-pack');
        const pricing = this.getPackCardPricing(jars);
        const priceEl = card?.querySelector('[data-byb-pack-price]');
        const compareEl = card?.querySelector('[data-byb-pack-compare]');
        const saveEl = card?.querySelector('[data-byb-pack-save]');
        if (priceEl) priceEl.textContent = this.money(pricing.current);
        if (compareEl) {
          if (pricing.compare > pricing.current) {
            compareEl.hidden = false;
            compareEl.textContent = this.money(pricing.compare);
          } else {
            compareEl.hidden = true;
            compareEl.textContent = '';
          }
        }
        if (saveEl) {
          if (pricing.savePercent > 0) {
            saveEl.hidden = false;
            saveEl.textContent = `Save ${pricing.savePercent}%`;
          } else {
            saveEl.hidden = true;
            saveEl.textContent = '';
          }
        }
      });
    }

    renderPurchase() {
      this.querySelectorAll('.byb-purchase-card').forEach((card) => {
        const input = card.querySelector('[data-byb-mode]');
        card.classList.toggle('is-selected', Boolean(input?.checked));
      });

      this.subscribePercentEls.forEach((el) => {
        el.textContent = String(this.getSelectedSubscribeDisplayPercent());
      });

      if (this.planInput) {
        this.planInput.value = this.purchaseMode === 'subscribe' ? this.sellingPlanId : '';
      }
    }

    renderTotals() {
      const pricing = this.getPricing();
      if (this.totalPriceEl) this.totalPriceEl.textContent = this.money(pricing.current);

      if (this.totalCompareEl) {
        if (pricing.compare > pricing.current) {
          this.totalCompareEl.hidden = false;
          this.totalCompareEl.textContent = this.money(pricing.compare);
        } else {
          this.totalCompareEl.hidden = true;
          this.totalCompareEl.textContent = '';
        }
      }

      if (this.totalSaveEl) {
        const showSave = pricing.savings > 0 && this.purchaseMode === 'subscribe';
        this.totalSaveEl.classList.toggle('is-visible', showSave);
        if (showSave) this.totalSaveEl.textContent = `You're saving ${this.money(pricing.savings)}`;
      }

      const remaining = Math.max(this.packSize - this.getTotalSelected(), 0);
      const complete = remaining === 0 && this.packSize > 0;
      if (this.atcBtn) {
        this.atcBtn.disabled = !complete;
        this.atcBtn.setAttribute('aria-disabled', complete ? 'false' : 'true');
      }
      if (this.atcLabelEl) {
        this.atcLabelEl.textContent = complete
          ? this.atcLabel
          : `Add ${remaining} more to unlock`;
      }
    }

    render() {
      this.renderCards();
      this.renderSlots();
      this.renderProgress();
      this.renderChecklist();
      this.renderPackPrices();
      this.renderPurchase();
      this.renderTotals();
    }

    getPackVariantId(jars) {
      const table = this.config.packVariantIds || {};
      return Number(table[String(jars)]) || 0;
    }

    buildProperties() {
      const prefix = this.config.selectionPropertyPrefix || 'Selection ';
      const slots = this.buildSlotList().filter(Boolean);
      const properties = {
        'Box Size': `${this.packSize} Pack`,
        _build_box: 'true',
        _pack_size: String(this.packSize),
      };

      const selectionTitles = [];
      slots.forEach((slot, index) => {
        const n = index + 1;
        const flavor = this.getFlavorById(slot.flavorId);
        const title = slot.title || flavor?.title || '';
        const variantId = Number(flavor?.variant_id) || 0;
        if (title) {
          properties[`${prefix}${n}`] = title;
          selectionTitles.push(title);
        }
        if (variantId) properties[`_bundle_item_${n}`] = String(variantId);
      });

      if (selectionTitles.length) {
        properties._bundle_selection = selectionTitles.join(' <> ');
      }

      return properties;
    }

    buildCartItems() {
      const packVariantId = this.getPackVariantId(this.packSize);
      if (!packVariantId) return [];

      const slots = this.buildSlotList().filter(Boolean);
      const missingVariant = slots.some((slot) => {
        const flavor = this.getFlavorById(slot.flavorId);
        return !Number(flavor?.variant_id);
      });
      if (missingVariant || slots.length !== this.packSize) return [];

      const item = {
        id: packVariantId,
        quantity: 1,
        properties: this.buildProperties(),
      };

      if (this.purchaseMode === 'subscribe') {
        const packPlan =
          this.getPackPlanPool().find((plan) => String(plan.id) === String(this.sellingPlanId)) ||
          this.getPackPlanPool()[0];
        if (packPlan?.id) item.selling_plan = Number(packPlan.id);
      }

      return [item];
    }

    setAtcBusy(label) {
      this.atcBtn?.classList.add('is-loading');
      if (this.atcBtn) {
        this.atcBtn.disabled = true;
        this.atcBtn.setAttribute('aria-disabled', 'true');
      }
      if (this.atcLabelEl && label) this.atcLabelEl.textContent = label;
    }

    getCartRoot() {
      return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    }

    normalizeCartAddResponse(data) {
      if (Array.isArray(data)) {
        const first = data[0] || {};
        return { ...first, items: data };
      }
      if (data?.items && Array.isArray(data.items) && !data.id && data.items[0]) {
        return { ...data.items[0], sections: data.sections, items: data.items };
      }
      return data || {};
    }

    cartAddFailed(response, data) {
      if (!response?.ok) return true;
      if (data?.status && Number(data.status) >= 400) return true;
      if (data?.errors) return true;
      if (data?.message === 'Cart Error') return true;
      if (data?.description && !data?.id && !data?.items) return true;
      return false;
    }

    cartErrorMessage(data) {
      if (typeof data?.description === 'string' && data.description) return data.description;
      if (typeof data?.message === 'string' && data.message && data.message !== 'Cart Error') return data.message;
      if (typeof data?.errors === 'string' && data.errors) return data.errors;
      return 'Could not add box to cart.';
    }

    async postCartItems(items) {
      const cart = this.getCartRoot();
      const payload = { items };
      if (cart && typeof cart.getSectionsToRender === 'function') {
        payload.sections = cart.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
        if (typeof cart.setActiveElement === 'function') {
          cart.setActiveElement(this.atcBtn || document.activeElement);
        }
      }

      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });

      const text = await response.text();
      let data = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (error) {
        data = { status: response.status || 500, message: 'Could not add box to cart.' };
      }

      return { response, data: this.normalizeCartAddResponse(data) };
    }

    async openCartAfterAdd(data, items) {
      const cart = this.getCartRoot();
      const variantId = items[0]?.id;

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'build-your-box',
          productVariantId: variantId,
          cartData: data,
        });
      }

      if (!cart) {
        window.location.href = window.routes?.cart_url || '/cart';
        return;
      }

      cart.classList?.remove('is-empty');

      if (typeof cart.renderContents === 'function' && data?.sections) {
        cart.renderContents(data);
        return;
      }

      if (typeof cart.refreshFromSectionFetch === 'function') {
        await cart.refreshFromSectionFetch();
      }

      if (typeof cart.open === 'function') cart.open(this.atcBtn);
    }

    async addToCart() {
      if (this._adding) return;
      if (this.atcBtn?.disabled || this.atcBtn?.getAttribute('aria-disabled') === 'true') return;

      if (this.getTotalSelected() !== this.packSize) {
        this.showError(`Select exactly ${this.packSize} jars to continue.`);
        return;
      }

      if (!this.getPackVariantId(this.packSize)) {
        this.showError('Select the pack product in the theme editor so this box can be added to cart.');
        return;
      }

      let items = this.buildCartItems().filter((item) => Number(item?.id) > 0);
      if (!items.length) {
        this.showError('Could not add this box. Make sure each selected flavor is available to purchase.');
        return;
      }

      this._adding = true;
      this.clearError();
      this.setAtcBusy('ADDING...');

      try {
        let { response, data } = await this.postCartItems(items);

        if (this.cartAddFailed(response, data) && items.some((item) => item.selling_plan)) {
          items = items.map(({ selling_plan, ...rest }) => rest);
          ({ response, data } = await this.postCartItems(items));
        }

        if (this.cartAddFailed(response, data)) {
          this.showError(this.cartErrorMessage(data));
          return;
        }

        await this.openCartAfterAdd(data, items);
      } catch (error) {
        console.error(error);
        this.showError('Something went wrong. Please try again.');
      } finally {
        this._adding = false;
        this.atcBtn?.classList.remove('is-loading');
        this.renderTotals();
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.hidden = false;
      this.errorEl.textContent = message;
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    escapeAttr(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }
  }

  if (!customElements.get('build-your-box')) {
    customElements.define('build-your-box', BuildYourBox);
  }
})();
